import React, { useEffect, useState } from "react";
import { Inbox, Newspaper, Images, Quote, Loader2, Megaphone } from "lucide-react";
import { api } from "../api";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const STATUS_LABELS = { new: "New", in_progress: "In progress", replied: "Replied", closed: "Closed" };

export default function OverviewTab({ onNavigate }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats(false));
  }, []);

  if (stats === null)
    return (
      <div className="flex items-center gap-2 text-stone-600 py-10">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading overview…
      </div>
    );
  if (stats === false) return <p className="text-stone-600 py-10">Could not load stats.</p>;

  const cards = [
    { label: "New enquiries", value: stats.enquiries_new, sub: `${stats.enquiries_week} this week · ${stats.enquiries_total} total`, icon: Inbox, tab: "enquiries" },
    { label: "News posts", value: stats.news_count, sub: "Published & drafts", icon: Newspaper, tab: "news" },
    { label: "Gallery photos", value: stats.gallery_count, sub: "Across all sections", icon: Images, tab: "gallery" },
    { label: "Testimonials", value: stats.testimonials_count, sub: "On the homepage", icon: Quote, tab: "testimonials" },
  ];

  return (
    <div className="space-y-8" data-testid="overview-tab">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <button
            key={c.label}
            onClick={() => onNavigate(c.tab)}
            className="text-left rounded-2xl bg-white border border-stone-200 p-5 hover:border-orange-400 transition-colors"
            data-testid={`overview-card-${c.tab}`}
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50 text-orange-700">
              <c.icon className="h-5 w-5" aria-hidden="true" />
            </span>
            <p className="mt-3 text-3xl font-heading font-bold text-stone-900">{c.value}</p>
            <p className="font-medium text-stone-800">{c.label}</p>
            <p className="text-sm text-stone-500">{c.sub}</p>
          </button>
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl bg-white border border-stone-200 p-5 flex items-center justify-between">
          <div>
            <p className="font-heading font-semibold text-stone-900">Recruitment</p>
            <p className={`text-sm font-medium ${stats.recruiting ? "text-green-700" : "text-red-700"}`}>
              {stats.recruiting ? "Open" : "Closed"}
            </p>
          </div>
          <button onClick={() => onNavigate("recruitment")} className="px-4 py-2 rounded-lg border-2 border-stone-300 text-sm font-semibold hover:border-orange-600" data-testid="overview-recruitment-link">
            Manage
          </button>
        </div>
        <div className="rounded-2xl bg-white border border-stone-200 p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Megaphone className="h-5 w-5 text-amber-700" aria-hidden="true" />
            <div>
              <p className="font-heading font-semibold text-stone-900">Site notice banner</p>
              <p className={`text-sm font-medium ${stats.notice_enabled ? "text-amber-700" : "text-stone-500"}`}>
                {stats.notice_enabled ? "Showing on the website" : "Off"}
              </p>
            </div>
          </div>
          <button onClick={() => onNavigate("settings")} className="px-4 py-2 rounded-lg border-2 border-stone-300 text-sm font-semibold hover:border-orange-600" data-testid="overview-notice-link">
            Manage
          </button>
        </div>
      </div>

      <div>
        <h3 className="font-heading text-lg font-semibold text-stone-900">Latest enquiries</h3>
        <div className="mt-3 rounded-2xl bg-white border border-stone-200 divide-y divide-stone-100" data-testid="overview-recent-enquiries">
          {stats.recent_enquiries.length === 0 ? (
            <p className="p-5 text-stone-600">No enquiries yet.</p>
          ) : (
            stats.recent_enquiries.map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <p className="font-medium text-stone-900 truncate">{e.name}</p>
                  <p className="text-sm text-stone-500">{e.enquiry_type} · {fmtDate(e.created_at)}</p>
                </div>
                <span className="rounded-full bg-stone-100 px-3 py-1 text-sm text-stone-700 shrink-0">
                  {STATUS_LABELS[e.status] || e.status}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
